import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Video, Plus, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import FormationFilterEditor, { Filter as StudentFilter } from "@/components/FormationFilterEditor";
import { API_URL, getAuthHeader } from "@/config";
import { toParisNaive, utcToParisNaive } from "@/lib/utils";
import VirtualClassTable from "@/components/virtual-classes/VirtualClassTable";
import ClassConfigModal from "@/components/virtual-classes/ClassConfigModal";
import { CsvStudent } from "@/components/virtual-classes/CsvStudentUploader";
import PreviewModal from "@/components/virtual-classes/PreviewModal";
import StatsModal from "@/components/virtual-classes/StatsModal";
import VirtualClassSettings from "@/components/virtual-classes/VirtualClassSettings";
import ValidationWizard from "@/components/virtual-classes/ValidationWizard";
import { Settings as SettingsIcon } from "lucide-react";

export type { CsvStudent } from "@/components/virtual-classes/CsvStudentUploader";

export interface Notification {
    id: string;
    subject: string;
    body: string;
    scheduling: {
        type: 'fixed' | 'before_start' | 'after_start' | 'before_end' | 'after_end';
        date?: string;
        offset?: number;
        unit?: 'minutes' | 'hours';
    }
}

export interface VirtualClass {
    id: string;
    name: string;
    url: string;
    start_time: string | null;
    end_time: string | null;
    created_by: string;
    created_at: string;
    status: 'active' | 'inactive';
    edit_status: 'draft' | 'validated';
    validated_at: string | null;
    student_source?: 'learnybox' | 'csv';
    csv_students?: CsvStudent[];
    // Backwards compatibility for frontend
    startTime?: string;
    endTime?: string;
    createdAt?: string;
    createdBy?: string;
}

const VirtualClasses: React.FC = () => {
    const { user, token, hasPermission, hasRole } = useAuth();
    const [classes, setClasses] = useState<VirtualClass[]>([]);
    const [className, setClassName] = useState("");
    const [classUrl, setClassUrl] = useState("");
    const [organizerEmail, setOrganizerEmail] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [editingClass, setEditingClass] = useState<VirtualClass | null>(null);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [configMode, setConfigMode] = useState<'create' | 'edit'>('create');
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    // CSV student source
    const [studentSource, setStudentSource] = useState<'learnybox' | 'csv'>('learnybox');
    const [csvStudents, setCsvStudents] = useState<CsvStudent[]>([]);

    // New states for notifications
    const [formations, setFormations] = useState<any[]>([]);
    const [selectedFormations, setSelectedFormations] = useState<number[]>([]);
    const [isFetchingFormations, setIsFetchingFormations] = useState(false);
    const [formationSearch, setFormationSearch] = useState("");

    // Filtering states
    const [formationFilters, setFormationFilters] = useState<Record<number, StudentFilter[]>>({});
    const [editingFiltersFor, setEditingFiltersFor] = useState<number | null>(null);

    // Phase 2 states
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [previewData, setPreviewData] = useState<{ student_count: number; sample_email: { subject: string; body: string } | null } | null>(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const [showStatsModal, setShowStatsModal] = useState(false);
    const [statsData, setStatsData] = useState<any>(null);
    const [isFetchingStats, setIsFetchingStats] = useState(false);
    const [selectedClassForStats, setSelectedClassForStats] = useState<string | null>(null);

    // EduSign resync
    const [resyncingClassId, setResyncingClassId] = useState<string | null>(null);

    // Filtering & Sorting
    const [hidePast, setHidePast] = useState(true);
    const [sortBy, setSortBy] = useState<'start_time' | 'created_at'>('start_time');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const handleSort = (column: 'start_time' | 'created_at') => {
        if (sortBy === column) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
    };

    const filteredClasses = hidePast
        ? classes.filter(cls => {
            const endOrStart = cls.end_time || cls.endTime || cls.start_time || cls.startTime;
            if (!endOrStart) return true; // keep unscheduled classes
            return new Date(endOrStart).getTime() > Date.now();
        })
        : classes;

    const sortedClasses = [...filteredClasses].sort((a, b) => {
        const getVal = (cls: VirtualClass) => {
            if (sortBy === 'start_time') return cls.start_time || cls.startTime || '';
            return cls.created_at || cls.createdAt || '';
        };
        const aVal = new Date(getVal(a)).getTime() || 0;
        const bVal = new Date(getVal(b)).getTime() || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    // Draft validation & view details
    const [validatingClass, setValidatingClass] = useState<VirtualClass | null>(null);
    const [viewingClass, setViewingClass] = useState<VirtualClass | null>(null);

    // MULTI-EMAIL NOTIFICATIONS
    const [notifications, setNotifications] = useState<Notification[]>([
        {
            id: crypto.randomUUID(),
            subject: "Invitation : Votre classe virtuelle approche",
            body: `
                <p>Bonjour {{first_name}},</p>
                <p>Vous êtes invité(e) à participer à la classe virtuelle <strong>{{class_name}}</strong>.</p>
                <p>Ces sessions sont de véritables temps d'entraînement, pensés pour vous aider à faire des liens entre les systèmes, les outils naturopathiques et la pratique de terrain, en partant de situations concrètes.</p>
                <p>👉 Lien d'accès personnel :<br/><a href="{{access_url}}">{{access_url}}</a></p>
                <p>🕒 Début de la session : {{start_time}}</p>
                <p>🧡 <strong>Information importante</strong></p>
                <p>Ce mail est automatique et, même s'il est envoyé avec amour, merci de ne pas répondre à ce message.</p>
                <p>Pour toute question technique ou logistique, merci de contacter directement :<br/>👉 <a href="mailto:contact@altermedic-center.com">contact@altermedic-center.com</a></p>
                <p>Si vous ne pouvez pas être présent(e), pas d'inquiétude : la session est enregistrée et sera disponible dans votre espace formation, rubrique "Rediffusion des classes virtuelles".</p>
                <p>Au plaisir de vous retrouver pour ce temps de pratique 🌱</p>
                <p>Chaleureusement,<br/>${user?.name || user?.username || ''}</p>
            `,
            scheduling: { type: 'before_start', offset: 2880, unit: 'minutes' }
        },
        {
            id: crypto.randomUUID(),
            subject: "Rappel : Votre classe virtuelle commence dans quelques minutes",
            body: `
                <p>Bonjour {{first_name}},</p>
                <p>Petit rappel : la session <strong>{{class_name}}</strong> commence dans 15 minutes 😊</p>
                <p>👉 Lien d'accès à la classe virtuelle :<br/><a href="{{access_url}}">{{access_url}}</a></p>
                <p>🕒 Début de la session : {{start_time}}</p>
                <p>🧡 Ce message est un mail automatique, merci de ne pas y répondre.</p>
                <p>En cas de problème technique ou de question logistique, contactez directement :<br/>👉 <a href="mailto:contact@altermedic-center.com">contact@altermedic-center.com</a></p>
                <p>Rappelez-vous : cette séance est enregistrée et sera rediffusée sur votre espace de formation.</p>
                <p>Installez-vous confortablement, avec de quoi noter… et votre boisson chaude ! ☕️</p>
                <p>À tout de suite 🌱</p>
                <p>Chaleureusement,<br/>${user?.name || user?.username || ''}</p>
            `,
            scheduling: { type: 'before_start', offset: 15, unit: 'minutes' }
        }
    ]);

    const addNotification = () => {
        setNotifications([...notifications, {
            id: crypto.randomUUID(),
            subject: "Rappel: Votre session commence bientôt",
            body: `<p>Bonjour {{first_name}}, un petit rappel pour votre session {{class_name}} qui commence dans 15 minutes.</p>`,
            scheduling: { type: 'before_start', offset: 15, unit: 'minutes' }
        }]);
    };

    const removeNotification = (id: string) => {
        if (notifications.length > 1) {
            setNotifications(notifications.filter(n => n.id !== id));
        } else {
            toast.error("Au moins une notification est requise");
        }
    };

    const updateNotification = (id: string, updates: Partial<Notification>) => {
        setNotifications(notifications.map(n => n.id === id ? { ...n, ...updates } : n));
    };

    const [currentStep, setCurrentStep] = useState(1);
    const [sendInvitations, setSendInvitations] = useState(true);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [step2Ready, setStep2Ready] = useState(false);

    const handleNextStep = (e?: React.MouseEvent | React.FormEvent) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }

        const hasStudents = studentSource === 'learnybox'
            ? selectedFormations.length > 0
            : csvStudents.length > 0;
        if (!className.trim() || !organizerEmail.trim() || !startTime || !endTime || !hasStudents) {
            return;
        }

        setIsTransitioning(true);
        setCurrentStep(2);
        setTimeout(() => {
            setIsTransitioning(false);
            setStep2Ready(true);
        }, 500);
    };

    const fetchClasses = async () => {
        if (!token) return;
        try {
            const response = await fetch(`${API_URL}/virtual-classes`, {
                headers: getAuthHeader(token),
            });
            if (response.ok) {
                const data = await response.json();
                const isAdmin = hasRole('admin');
                setClasses(isAdmin ? data : data.filter((c: VirtualClass) => c.created_by === user?.username || c.created_by === user?.email));
            } else {
                toast.error("Impossible de récupérer les classes");
            }
        } catch (error) {
            console.error("Failed to fetch classes", error);
            toast.error("Erreur de connexion au serveur");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFormations = async () => {
        if (!token) return;
        setIsFetchingFormations(true);
        try {
            const response = await fetch(`${API_URL}/learnybox/courses`, {
                headers: getAuthHeader(token),
            });
            if (response.ok) {
                const data = await response.json();
                setFormations(data);
            }
        } catch (error) {
            console.error("Failed to fetch formations", error);
            toast.error("Impossible de récupérer les formations Learnybox");
        } finally {
            setIsFetchingFormations(false);
        }
    };

    useEffect(() => {
        fetchClasses();
        fetchFormations();
        if (user?.email) {
            setOrganizerEmail(user.email);
        }
    }, [token, user]);

    useEffect(() => {
        if (showConfigModal && configMode === 'create') {
            setCurrentStep(1);
            setStep2Ready(false);
            setSendInvitations(true);
        }
        if (showConfigModal && configMode === 'create') {
            if (!className) setClassName(`Nouvelle Classe - ${new Date().toLocaleDateString('fr-FR')}`);

            if (!startTime) {
                const start = new Date();
                start.setHours(start.getHours() + 1, 0, 0, 0);
                setStartTime(toParisNaive(start));
            }
            if (!endTime) {
                const end = new Date();
                end.setHours(end.getHours() + 2, 0, 0, 0);
                setEndTime(toParisNaive(end));
            }
        }
    }, [showConfigModal, configMode]);

    const submitVirtualClass = async (e: React.FormEvent | React.MouseEvent) => {
        if (e) e.preventDefault();
        // Step 3 is Recap, but submission happens there (or from Step 2 if user bypassed)
        if (isSubmitting || isTransitioning) return;
        if (!className.trim()) return;

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/virtual-classes`, {
                method: 'POST',
                headers: getAuthHeader(token),
                body: JSON.stringify({
                    name: className,
                    url: classUrl,
                    start_time: startTime || null,
                    end_time: endTime || null,
                    created_by: user?.username || "Unknown",
                    status: 'active',
                    email: organizerEmail,
                    student_source: studentSource,
                    ...(studentSource === 'learnybox'
                        ? {
                            formations_config: selectedFormations.reduce((acc, id) => {
                                acc[id] = formationFilters[id] || [];
                                return acc;
                            }, {} as Record<number, StudentFilter[]>),
                        }
                        : { csv_students: csvStudents }
                    ),
                    notifications: sendInvitations ? notifications : [],
                }),
            });

            if (response.ok) {
                await fetchClasses();
                setShowConfigModal(false);
                setClassName("");
                setClassUrl("");
                if (!user?.email) setOrganizerEmail("");
                setStartTime("");
                setEndTime("");
                setStudentSource('learnybox');
                setCsvStudents([]);
                setSelectedFormations([]);
                setFormationFilters({});
                setNotifications([
                    {
                        id: crypto.randomUUID(),
                        subject: "Invitation : Votre classe virtuelle approche",
                        body: `
                            <p>Bonjour {{first_name}},</p>
                            <p>Vous êtes invité(e) à participer à la classe virtuelle <strong>{{class_name}}</strong>.</p>
                            <p>Ces sessions sont de véritables temps d'entraînement, pensés pour vous aider à faire des liens entre les systèmes, les outils naturopathiques et la pratique de terrain, en partant de situations concrètes.</p>
                            <p>👉 Lien d'accès personnel :<br/><a href="{{access_url}}">{{access_url}}</a></p>
                            <p>🕒 Début de la session : {{start_time}}</p>
                            <p>🧡 <strong>Information importante</strong></p>
                            <p>Ce mail est automatique et, même s'il est envoyé avec amour, merci de ne pas répondre à ce message.</p>
                            <p>Pour toute question technique ou logistique, merci de contacter directement :<br/>👉 <a href="mailto:contact@altermedic-center.com">contact@altermedic-center.com</a></p>
                            <p>Si vous ne pouvez pas être présent(e), pas d'inquiétude : la session est enregistrée et sera disponible dans votre espace formation, rubrique "Rediffusion des classes virtuelles".</p>
                            <p>Au plaisir de vous retrouver pour ce temps de pratique 🌱</p>
                            <p>Chaleureusement,<br/>${user?.name || user?.username || ''}</p>
                        `,
                        scheduling: { type: 'before_start', offset: 2880, unit: 'minutes' }
                    },
                    {
                        id: crypto.randomUUID(),
                        subject: "Rappel : Votre classe virtuelle commence dans quelques minutes",
                        body: `
                            <p>Bonjour {{first_name}},</p>
                            <p>Votre classe virtuelle <strong>{{class_name}}</strong> commence dans quelques minutes !</p>
                            <p>Cliquez sur le lien ci-dessous pour rejoindre la session :</p>
                            <p><a href="{{access_url}}">{{access_url}}</a></p>
                            <p>À tout de suite !</p>
                        `,
                        scheduling: { type: 'before_start', offset: 15, unit: 'minutes' }
                    }
                ]);
                toast.success("Classe créée avec succès");
            } else {
                toast.error("Erreur lors de la création");
            }
        } catch (error) {
            toast.error("Erreur lors de la création");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClass = async (id: string) => {
        if (confirm("Êtes-vous sûr de vouloir supprimer cette classe ?")) {
            try {
                const response = await fetch(`${API_URL}/virtual-classes/${id}`, {
                    method: 'DELETE',
                    headers: getAuthHeader(token),
                });
                if (response.ok) {
                    setClasses(classes.filter((c) => c.id !== id));
                    toast.success("Classe supprimée");
                } else {
                    toast.error("Erreur lors de la suppression");
                }
            } catch (error) {
                toast.error("Erreur lors de la suppression");
            }
        }
    };

    const handleToggleStatus = async (cls: VirtualClass) => {
        const newStatus = cls.status === 'active' ? 'inactive' : 'active';
        try {
            const response = await fetch(`${API_URL}/virtual-classes/${cls.id}`, {
                method: 'PUT',
                headers: getAuthHeader(token),
                body: JSON.stringify({ status: newStatus }),
            });
            if (response.ok) {
                const updatedClass = await response.json();
                setClasses(classes.map(c => c.id === cls.id ? updatedClass : c));
                toast.success("Statut mis à jour");
            } else {
                toast.error("Erreur lors de la mise à jour");
            }
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const handleClassValidated = (updatedClass: VirtualClass) => {
        setClasses(classes.map(c => c.id === updatedClass.id ? updatedClass : c));
        setValidatingClass(null);
    };

    const handleResyncEdusign = async (cls: VirtualClass) => {
        setResyncingClassId(cls.id);
        try {
            const response = await fetch(`${API_URL}/virtual-classes/${cls.id}/resync-edusign`, {
                method: 'POST',
                headers: getAuthHeader(token),
            });
            const data = await response.json();
            if (response.ok) {
                toast.success("Synchronisation EduSign lancée en arrière-plan");
            } else {
                toast.error(data.error || "Erreur lors de la synchronisation EduSign");
            }
        } catch (error) {
            toast.error("Erreur de connexion au serveur");
        } finally {
            setResyncingClassId(null);
        }
    };

    const openEditModal = (cls: any) => {
        if (cls.edit_status === 'validated') {
            toast.error("Les classes validées ne peuvent plus être modifiées.");
            return;
        }
        setEditingClass(cls);
        setClassName(cls.name);
        setClassUrl(cls.url);
        setOrganizerEmail(cls.email || "");
        setStartTime(cls.start_time ? utcToParisNaive(cls.start_time) : "");
        setEndTime(cls.end_time ? utcToParisNaive(cls.end_time) : "");
        setStudentSource(cls.student_source || 'learnybox');
        setCsvStudents(cls.csv_students || []);
        setSelectedFormations(cls.formations_config ? Object.keys(cls.formations_config).map(Number) : []);
        setFormationFilters(cls.formations_config || {});

        // Handle conversion from legacy scheduling format if needed
        if (cls.notifications) {
            setNotifications(cls.notifications);
        } else if (cls.scheduling_config) {
            // Legacy fallback: convert one scheduling config to a notification
            setNotifications([{
                id: crypto.randomUUID(),
                subject: cls.email_subject || "Invitation",
                body: cls.email_body || "",
                scheduling: cls.scheduling_config[0] || { type: 'before_start', offset: 15, unit: 'minutes' }
            }]);
        }

        setConfigMode('edit');
        setShowConfigModal(true);
    };

    const handleUpdateClass = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingClass) return;
        try {
            const response = await fetch(`${API_URL}/virtual-classes/${editingClass.id}`, {
                method: 'PUT',
                headers: getAuthHeader(token),
                body: JSON.stringify({
                    name: className,
                    url: classUrl,
                    start_time: startTime || null,
                    end_time: endTime || null,
                    notifications: notifications,
                    student_source: studentSource,
                    ...(studentSource === 'learnybox'
                        ? {
                            formations_config: selectedFormations.reduce((acc, id) => {
                                acc[id] = formationFilters[id] || [];
                                return acc;
                            }, {} as Record<number, StudentFilter[]>),
                        }
                        : { csv_students: csvStudents }
                    ),
                }),
            });
            if (response.ok) {
                const updatedClass = await response.json();
                setClasses(classes.map(c => c.id === editingClass.id ? updatedClass : c));
                setShowConfigModal(false);
                setEditingClass(null);
                setClassName("");
                setClassUrl("");
                setStartTime("");
                setEndTime("");
                toast.success("Classe mise à jour");
            } else {
                toast.error("Erreur lors de la mise à jour");
            }
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const handlePreview = async (notif: Notification) => {
        if (!className.trim()) { toast.error("Veuillez entrer un nom pour la classe"); return; }
        setIsPreviewing(true);
        try {
            const response = await fetch(`${API_URL}/virtual-classes/preview`, {
                method: 'POST',
                headers: getAuthHeader(token),
                body: JSON.stringify({
                    name: className,
                    student_source: studentSource,
                    ...(studentSource === 'learnybox'
                        ? {
                            formations_config: selectedFormations.reduce((acc, id) => {
                                acc[id] = formationFilters[id] || [];
                                return acc;
                            }, {} as Record<number, StudentFilter[]>),
                        }
                        : { csv_students: csvStudents }
                    ),
                    email_subject: notif.subject,
                    email_body: notif.body,
                }),
            });
            if (response.ok) {
                const data = await response.json();
                setPreviewData(data);
                setShowPreviewModal(true);
            } else { toast.error("Erreur lors de la prévisualisation"); }
        } catch (error) {
            console.error("Preview failed", error);
        } finally { setIsPreviewing(false); }
    };

    const fetchStats = async (classId: string) => {
        setIsFetchingStats(true);
        setSelectedClassForStats(classId);
        try {
            const response = await fetch(`${API_URL}/virtual-classes/${classId}/stats`, {
                headers: getAuthHeader(token),
            });
            if (response.ok) {
                const data = await response.json();
                setStatsData(data);
                setShowStatsModal(true);
            }
        } catch (error) {
            console.error("Failed to fetch stats", error);
        } finally { setIsFetchingStats(false); }
    };

    return (
        <div className="mx-auto px-4 py-8 max-w-7xl animate-in fade-in duration-500 space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-[0.03]">
                    <div className="h-32 w-32 rotate-12"><Video className="h-full w-full" /></div>
                </div>
                <div className="z-10 flex w-full justify-between items-center">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">Classes Virtuelles</h1>
                        <p className="text-slate-500 font-medium mt-1">Gérez vos salles de réunion Google Meet.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setHidePast(!hidePast)}
                            className={`rounded-2xl h-12 px-4 border-slate-200 font-medium text-sm transition-all ${hidePast ? 'bg-primary/5 border-primary/20 text-primary' : 'text-slate-500 hover:text-slate-900'}`}
                            title={hidePast ? "Afficher les classes passées" : "Masquer les classes passées"}
                        >
                            {hidePast ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                            {hidePast ? 'Voir tout' : 'Masquer passées'}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setShowSettingsModal(true)}
                            className="rounded-2xl h-12 w-12 p-0 border-slate-200 text-slate-500 hover:text-slate-900 transition-all"
                        >
                            <SettingsIcon className="h-5 w-5" />
                        </Button>
                        <Button
                            onClick={() => {
                                setConfigMode('create');
                                setEditingClass(null);
                                setShowConfigModal(true);
                            }}
                            className="rounded-2xl h-12 px-6 font-bold shadow-lg shadow-primary/20 transition-all hover:scale-105 active:scale-95"
                        >
                            <Plus className="h-5 w-5 mr-2" /> Créer une Classe
                        </Button>
                    </div>
                </div>
            </div>

            <VirtualClassTable
                classes={sortedClasses}
                isLoading={isLoading}
                handleToggleStatus={handleToggleStatus}
                handleValidate={(cls) => setValidatingClass(cls)}
                handleResyncEdusign={handleResyncEdusign}
                resyncingClassId={resyncingClassId}
                fetchStats={fetchStats}
                viewDetails={(cls) => setViewingClass(cls)}
                openEditModal={openEditModal}
                handleDeleteClass={handleDeleteClass}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                canViewStats={hasPermission('view class stats')}
            />

            <ClassConfigModal
                mode={configMode}
                isOpen={showConfigModal}
                onOpenChange={setShowConfigModal}
                initialData={editingClass}

                // DATA
                className={className}
                setClassName={setClassName}
                organizerEmail={organizerEmail}
                setOrganizerEmail={setOrganizerEmail}
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                classUrl={classUrl}
                setClassUrl={setClassUrl}

                // STUDENT SOURCE
                studentSource={studentSource}
                setStudentSource={setStudentSource}
                csvStudents={csvStudents}
                setCsvStudents={setCsvStudents}

                // FORMATIONS
                formations={formations}
                isFetchingFormations={isFetchingFormations}
                formationSearch={formationSearch}
                setFormationSearch={setFormationSearch}
                selectedFormations={selectedFormations}
                setSelectedFormations={setSelectedFormations}
                formationFilters={formationFilters}
                setEditingFiltersFor={setEditingFiltersFor}

                // NOTIFICATIONS
                sendInvitations={sendInvitations}
                setSendInvitations={setSendInvitations}
                notifications={notifications}
                addNotification={addNotification}
                removeNotification={removeNotification}
                updateNotification={updateNotification}

                // UI
                isSubmitting={isSubmitting}
                onSubmit={configMode === 'create' ? submitVirtualClass : handleUpdateClass}
            />

            <PreviewModal
                isOpen={showPreviewModal}
                onOpenChange={setShowPreviewModal}
                previewData={previewData}
            />

            <StatsModal
                isOpen={showStatsModal}
                onOpenChange={setShowStatsModal}
                isFetchingStats={isFetchingStats}
                statsData={statsData}
            />

            {editingFiltersFor && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                    <FormationFilterEditor
                        formationId={editingFiltersFor}
                        formationName={formations.find(f => f.id === editingFiltersFor)?.titre || formations.find(f => f.id === editingFiltersFor)?.nom || ""}
                        filters={formationFilters[editingFiltersFor] || []}
                        onUpdate={(filters) => setFormationFilters({ ...formationFilters, [editingFiltersFor]: filters })}
                        onClose={() => setEditingFiltersFor(null)}
                    />
                </div>
            )}

            <VirtualClassSettings
                isOpen={showSettingsModal}
                onOpenChange={setShowSettingsModal}
                token={token}
            />

            <ValidationWizard
                virtualClass={validatingClass}
                isOpen={!!validatingClass}
                onOpenChange={(open) => { if (!open) setValidatingClass(null); }}
                token={token}
                onValidated={handleClassValidated}
            />

            <ValidationWizard
                virtualClass={viewingClass}
                isOpen={!!viewingClass}
                onOpenChange={(open) => { if (!open) setViewingClass(null); }}
                token={token}
                mode="view"
            />
        </div>
    );
};

export default VirtualClasses;

